import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, RefreshCw, ExternalLink, AlertCircle, IndianRupee } from "lucide-react";
import { adminBookingDetailUrl } from "@/constants/adminRoutes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "sarathix_admin_token";

const STATUS_LABELS = {
  paid: { label: "Paid", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  created: { label: "Link Sent", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  partially_paid: { label: "Partial", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  expired: { label: "Expired", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
  cancelled: { label: "Cancelled", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
  refunded: { label: "Refunded", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  refund_failed: { label: "Refund Failed", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  failed: { label: "Failed", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};

const SOURCE_LABELS = {
  razorpay: "Razorpay",
  upi_offline: "UPI (offline)",
  cash: "Cash",
};

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [totals, setTotals] = useState({ paid: 0, pending: 0, count: 0 });
  const [rzpConfigured, setRzpConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const authHeader = useCallback(() => {
    const t = sessionStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusResp, listResp] = await Promise.all([
        axios.get(`${API}/admin/payments/status`, { headers: authHeader() }),
        axios.get(`${API}/admin/payments`, { headers: authHeader(), params: statusFilter ? { status: statusFilter } : {} }),
      ]);
      setRzpConfigured(!!statusResp.data?.razorpay_configured);
      setPayments(listResp.data?.payments || []);
      setTotals(listResp.data?.totals || { paid: 0, pending: 0, count: 0 });
    } catch {
      toast.error("Couldn't load payments.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, authHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  };

  return (
    <div className="space-y-6" data-testid="admin-payments-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-white tracking-tight">Payments</h1>
          <p className="text-sarathi-text-secondary text-sm mt-1">
            Razorpay payment links and offline (UPI / cash) reconciliation.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchData}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm hover:bg-white/10 transition-colors"
          data-testid="payments-refresh"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {!rzpConfigured && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 flex gap-3" data-testid="rzp-not-configured-banner">
          <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
          <div className="text-sm">
            <p className="text-amber-200 font-semibold">Razorpay is not configured yet.</p>
            <p className="text-amber-200/80 mt-1">
              Set <code className="px-1 bg-black/30 rounded">RAZORPAY_KEY_ID</code>,{" "}
              <code className="px-1 bg-black/30 rounded">RAZORPAY_KEY_SECRET</code>, and{" "}
              <code className="px-1 bg-black/30 rounded">RAZORPAY_WEBHOOK_SECRET</code> in your deployment Secrets panel.
              Until then, only offline (UPI/cash) reconciliation is available.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4">
          <p className="text-[11px] uppercase tracking-wider text-sarathi-text-muted font-semibold flex items-center gap-1.5">
            <IndianRupee className="w-3 h-3" />
            Collected
          </p>
          <p className="text-2xl font-bold text-emerald-400 mt-1" data-testid="payments-paid-total">{formatINR(totals.paid)}</p>
        </div>
        <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4">
          <p className="text-[11px] uppercase tracking-wider text-sarathi-text-muted font-semibold flex items-center gap-1.5">
            <CreditCard className="w-3 h-3" />
            Pending
          </p>
          <p className="text-2xl font-bold text-amber-400 mt-1" data-testid="payments-pending-total">{formatINR(totals.pending)}</p>
        </div>
        <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4">
          <p className="text-[11px] uppercase tracking-wider text-sarathi-text-muted font-semibold">Total Records</p>
          <p className="text-2xl font-bold text-white mt-1">{totals.count}</p>
        </div>
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <span className="text-xs text-sarathi-text-muted uppercase tracking-wider font-semibold">Filter:</span>
        {[
          { v: "", l: "All" },
          { v: "paid", l: "Paid" },
          { v: "created", l: "Link Sent" },
          { v: "expired", l: "Expired" },
          { v: "refunded", l: "Refunded" },
        ].map((f) => (
          <button
            type="button"
            key={f.v || "all"}
            onClick={() => setStatusFilter(f.v)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              statusFilter === f.v
                ? "bg-sarathi-primary text-white border-sarathi-primary"
                : "bg-white/5 text-sarathi-text-secondary border-white/10 hover:bg-white/10"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      <div className="bg-sarathi-surface border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sarathi-text-muted text-sm">Loading…</div>
        ) : payments.length === 0 ? (
          <div className="p-12 text-center">
            <CreditCard className="w-10 h-10 text-sarathi-text-muted mx-auto mb-3 opacity-40" />
            <p className="text-sarathi-text-secondary text-sm">No payments yet.</p>
            <p className="text-sarathi-text-muted text-xs mt-1">Use the booking detail page to send a payment link or record an offline payment.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/[0.03] border-b border-white/10 text-xs uppercase tracking-wider text-sarathi-text-muted">
                  <th className="text-left font-semibold px-4 py-3">Booking</th>
                  <th className="text-left font-semibold px-4 py-3">Amount</th>
                  <th className="text-left font-semibold px-4 py-3">Source</th>
                  <th className="text-left font-semibold px-4 py-3">Status</th>
                  <th className="text-left font-semibold px-4 py-3">Created</th>
                  <th className="text-right font-semibold px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => {
                  const statusInfo = STATUS_LABELS[p.status] || { label: p.status, cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" };
                  return (
                    <tr key={p.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3">
                        <a href={adminBookingDetailUrl(p.booking_reference)} className="text-white font-mono hover:text-sarathi-primary transition-colors">
                          {p.booking_reference}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-white font-semibold">{formatINR(p.amount)}</td>
                      <td className="px-4 py-3 text-sarathi-text-secondary">{SOURCE_LABELS[p.source] || p.source}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-semibold border ${statusInfo.cls}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sarathi-text-muted text-xs whitespace-nowrap">{formatDate(p.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        {p.short_url && (
                          <a
                            href={p.short_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-blue-500/10 border border-blue-500/30 text-blue-400 text-[11px] font-semibold hover:bg-blue-500/20 transition-colors"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Open Link
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
